/**
 * 国际化路由加载器集成
 * 为 React Router v7 提供国际化支持
 */

import type { LoaderFunctionArgs } from 'react-router';
import type { I18nClient } from './i18n';
import type {
    PageHandle,
    I18nHandle,
    RouteI18nData,
    I18nRouteLoaderOptions,
    EnhanceRoutesI18nOptions
} from './types';
import { useLoaderData, useLocation } from 'react-router';

/**
 * 创建国际化路由 loader
 * 用于在路由加载时预加载所需的翻译资源
 */
export function createI18nRouteLoader(
    i18nClient: I18nClient,
    options?: I18nRouteLoaderOptions
) {
    return async ({ request, params }: LoaderFunctionArgs) => {
        const url = new URL(request.url);
        const searchParams = url.searchParams;

        // 从 URL 参数或路径参数中获取语言
        const urlLocale = searchParams.get('locale') || params.locale;

        // 从 localStorage/sessionStorage 获取保存的语言
        const savedLocale = typeof window !== 'undefined'
            ? localStorage.getItem(i18nClient.config.persistence?.key || 'user_locale')
            : null;

        // 确定要加载的语言
        const targetLocale = urlLocale || savedLocale || i18nClient.config.defaultLocale;

        // 预加载翻译资源
        if (options?.preloadAll) {
            await i18nClient.preloadResources();
        } else {
            const localesToLoad = options?.locales || [targetLocale];
            await i18nClient.preloadResources(localesToLoad);
        }

        // 如果有延迟设置（用于演示加载状态）
        if (options?.delay && options.delay > 0) {
            await new Promise(resolve => setTimeout(resolve, options.delay));
        }

        return {
            locale: targetLocale,
            i18nLoaded: true
        };
    };
}

/**
 * 创建带国际化的 defer loader
 * 支持 React Router v7 的 defer 功能
 */
export function createDeferredI18nLoader(
    i18nClient: I18nClient,
    dataLoader?: (args: LoaderFunctionArgs) => Promise<unknown>
) {
    return async ({ request, params }: LoaderFunctionArgs) => {
        const url = new URL(request.url);
        const locale = url.searchParams.get('locale') ||
            params.locale ||
            i18nClient.config.defaultLocale;

        // 延迟加载国际化资源
        const i18nPromise = i18nClient.preloadResources([locale]);

        // 如果有数据加载器，同时执行
        if (dataLoader) {
            const dataPromise = dataLoader({ request, params, context: {} });

            // 使用 defer 支持流式渲染
            return {
                locale,
                i18n: i18nPromise,
                data: dataPromise
            };
        }

        return {
            locale,
            i18n: i18nPromise
        };
    };
}

/**
 * 国际化路由 handle 增强
 * 自动处理页面标题的国际化
 */
export function createI18nHandle<Keys extends string = string>(
    baseHandle: Partial<PageHandle<Keys>> = {}
): I18nHandle<Keys> {
    return {
        // 继承基础 handle 的所有属性
        ...baseHandle,
        // 标记为需要国际化处理
        i18n: true,
        // 添加国际化元数据处理器
        getI18nMeta: (t: (key: string) => string) => {
            const meta = baseHandle.meta || {};
            const result: { title?: string; description?: string;[key: string]: unknown } = {};

            // 处理标题
            if (meta.title) {
                if (typeof meta.title === 'string') {
                    result.title = t(meta.title);
                } else if (typeof meta.title === 'object' && 'localizedId' in meta.title) {
                    result.title = t(meta.title.localizedId);
                }
            }

            // 处理描述
            if (meta.description) {
                if (typeof meta.description === 'string') {
                    result.description = t(meta.description);
                }
            }

            return result;
        }
    };
}

/**
 * 创建国际化路由配置
 * 为现有路由配置添加国际化支持
 */
export function enhanceRoutesWithI18n(
    routes: Record<string, unknown>[],
    i18nClient: I18nClient,
    options?: EnhanceRoutesI18nOptions
) {
    const { autoAddLoader = true } = options || {};

    return routes.map(route => {
        // 如果路由已经有 loader，增强它
        if (route.loader && autoAddLoader) {
            const originalLoader = route.loader as (args: LoaderFunctionArgs) => Promise<unknown>;
            route.loader = createDeferredI18nLoader(i18nClient, originalLoader);
        } else if (autoAddLoader && !route.loader) {
            // 如果没有 loader，添加一个简单的国际化 loader
            route.loader = createI18nRouteLoader(i18nClient);
        }

        // 如果有 handle，增强它以支持国际化
        if (route.handle) {
            route.handle = createI18nHandle(route.handle);
        }

        // 递归处理子路由
        if (route.children && Array.isArray(route.children)) {
            route.children = enhanceRoutesWithI18n(route.children as Record<string, unknown>[], i18nClient, options);
        }

        return route;
    });
}

/**
 * Hook: 在组件中使用路由国际化数据
 * 获取路由加载器提供的国际化数据和辅助方法
 */
export function useRouteI18n(): {
    /** 当前语言 */
    locale: string;
    /** 国际化资源是否已加载 */
    isLoaded: boolean;
    /** 获取当前路径的语言参数 */
    getLocaleFromPath: () => string | null;
    /** 切换语言的 URL 生成器 */
    getLocalizedUrl: (newLocale: string) => string;
    /** 等待国际化资源加载完成 */
    waitForI18n: () => Promise<void>;
} {
    const location = useLocation();
    const loaderData = useLoaderData() as RouteI18nData | undefined;

    // 从 loader 数据获取语言信息
    const locale = loaderData?.locale || 'en';
    const isLoaded = loaderData?.i18nLoaded || false;

    /**
     * 从当前路径获取语言参数
     */
    const getLocaleFromPath = (): string | null => {
        const searchParams = new URLSearchParams(location.search);
        const localeFromQuery = searchParams.get('locale');

        // 也可以从路径段中提取语言（如 /en/about, /zh/about）
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const localeFromPath = pathSegments[0];

        // 简单的语言代码验证（可以根据需要扩展）
        const isValidLocale = (code: string) => /^[a-z]{2}(-[A-Z]{2})?$/.test(code);

        return localeFromQuery ||
            (isValidLocale(localeFromPath) ? localeFromPath : null);
    };

    /**
     * 生成指定语言的 URL
     */
    const getLocalizedUrl = (newLocale: string): string => {
        const url = new URL(window.location.href);

        // 更新查询参数中的语言
        url.searchParams.set('locale', newLocale);

        return url.pathname + url.search;
    };

    /**
     * 等待国际化资源加载完成
     */
    const waitForI18n = async (): Promise<void> => {
        if (loaderData?.i18n && loaderData.i18n instanceof Promise) {
            await loaderData.i18n;
        }
    };

    return {
        locale,
        isLoaded,
        getLocaleFromPath,
        getLocalizedUrl,
        waitForI18n
    };
}

/**
 * 导出类型定义
 */
export type { LoaderFunctionArgs };